import type { SignalLevel } from '../components/SignalBars'
import type { CallQuality } from '../types'

/**
 * Aggregate the three main call-quality metrics into a 0..4 signal-bar level.
 *
 * Thresholds (picked from VoIP practice — see PR #2 Step 11 in the plan):
 *   4: RTT < 100ms, loss < 0.5%, jitter < 20ms
 *   3: RTT < 150ms, loss < 1%,   jitter < 30ms
 *   2: RTT < 300ms, loss < 3%,   jitter < 60ms
 *   1: anything worse
 *
 * Picks the WORST individual metric, not an average — a single spike pulls
 * the aggregate down. Perfect RTT is meaningless if 10% of packets are lost.
 * Level 0 is reserved for `iceConnectionState ∈ {disconnected, failed}` and
 * is not produced by this function.
 */
export function computeQuality(args: {
  rtt: number
  lossRatio: number
  jitter: number
}): SignalLevel {
  const { rtt, lossRatio, jitter } = args
  const byRtt = rtt < 100 ? 4 : rtt < 150 ? 3 : rtt < 300 ? 2 : 1
  const byLoss = lossRatio < 0.005 ? 4 : lossRatio < 0.01 ? 3 : lossRatio < 0.03 ? 2 : 1
  const byJitter = jitter < 20 ? 4 : jitter < 30 ? 3 : jitter < 60 ? 2 : 1
  return Math.min(byRtt, byLoss, byJitter) as SignalLevel
}

/**
 * Compute a 0–4 signal level from a persisted CallQuality record.
 *
 * Two paths:
 *   - v2 (aggregates present): voice-only p95 RTT/jitter + cumulative loss
 *     ratio + degraded-time downgrade. Returns null on pure-DTX calls
 *     (`dtx_ratio === 1` — there's no voice signal to score).
 *   - legacy (snapshots only): worst-of-3 over end-snapshot, but with a
 *     DTX guard — if the end snapshot looks like Opus silence (bitrate < 5
 *     kbps AND no loss between start and end), jitter is dropped from the
 *     formula so SID-frame spacing doesn't get mistaken for network jitter.
 *
 * Both paths return null when the record carries no usable data.
 */
export function computeQualityFromRecord(q: CallQuality | null): SignalLevel | null {
  if (!q) return null

  // v2 path — aggregates win when present. Use nullish check so legacy
  // test fixtures missing the v2 fields (undefined) fall through to the
  // legacy path instead of entering v2 with NaN inputs.
  if (q.rtt_p95_ms != null) {
    if (q.dtx_ratio === 1) return null
    const level = computeQuality({
      rtt: q.rtt_p95_ms,
      lossRatio: q.loss_ratio_avg ?? 0,
      jitter: q.jitter_p95_ms ?? 0,
    })
    // Cap at Fair when more than 10% of voice ticks were degraded — even
    // if averages look fine, lots of brief drops mean a bad call subjectively.
    if (q.loss_high_time_ratio != null && q.loss_high_time_ratio > 0.1) {
      return Math.min(level, 2) as SignalLevel
    }
    return level
  }

  // Legacy path — fallback for rows that don't have aggregates
  const rtt = q.end_rtt_ms ?? q.start_rtt_ms
  const jitter = q.end_jitter_ms ?? q.start_jitter_ms

  const lost = q.end_packets_lost ?? q.start_packets_lost
  const rcv = q.end_packets_received ?? q.start_packets_received
  const total = (lost !== null && rcv !== null) ? lost + rcv : null
  const lossRatio = (lost !== null && total !== null && total > 0) ? lost / total : null

  if (rtt === null && jitter === null && lossRatio === null) return null

  // DTX guard: end-snapshot looks like silence — bitrate ≪ voice baseline
  // AND loss didn't move between start and end. In those cases the jitter
  // reading is just SID-frame spacing, not real network jitter.
  const endIsDtx =
    q.end_bitrate_kbps !== null
    && q.end_bitrate_kbps < 5
    && q.end_packets_lost !== null
    && q.start_packets_lost !== null
    && q.end_packets_lost === q.start_packets_lost

  return computeQuality({
    rtt: rtt ?? 0,
    lossRatio: lossRatio ?? 0,
    jitter: endIsDtx ? 0 : (jitter ?? 0),
  })
}

export const qualityLabels: Record<SignalLevel, { label: string; color: string }> = {
  0: { label: '—', color: 'text-gray-400 dark:text-gray-500' },
  1: { label: 'Poor', color: 'text-red-500' },
  2: { label: 'Fair', color: 'text-yellow-500' },
  3: { label: 'Good', color: 'text-green-500' },
  4: { label: 'Excellent', color: 'text-green-500' },
}

/**
 * Human-readable tooltip for call quality — shown on hover.
 * Loss is displayed as percentage instead of raw packet counts.
 *
 * Two formats:
 *   - v2 (aggregates present): p50/p95 RTT, p95 jitter (voice-only), avg
 *     loss, % degraded time, avg PPS, % DTX, avg bitrate.
 *   - legacy (snapshots only): the original start→end style.
 */
export function qualityTooltip(q: CallQuality | null): string {
  if (!q) return 'No quality data'

  // v2 path
  if (q.rtt_p95_ms != null) {
    const lines: string[] = []
    const p50 = q.rtt_p50_ms != null ? Math.round(q.rtt_p50_ms) : '—'
    lines.push(`RTT: ${p50} / ${Math.round(q.rtt_p95_ms)} ms (p50 / p95)`)

    if (q.jitter_p95_ms != null) {
      const dtxNote = (q.dtx_ratio != null && q.dtx_ratio > 0.1) ? ' (p95, voice-only)' : ' (p95)'
      lines.push(`Jitter: ${Math.round(q.jitter_p95_ms)} ms${dtxNote}`)
    }
    if (q.loss_ratio_avg != null) {
      const lossPct = (q.loss_ratio_avg * 100).toFixed(1)
      const degTime = q.loss_high_time_ratio != null
        ? `, ${(q.loss_high_time_ratio * 100).toFixed(0)}% time degraded`
        : ''
      lines.push(`Loss: ${lossPct}% avg${degTime}`)
    }
    if (q.pps_avg != null) {
      lines.push(`PPS: ${Math.round(q.pps_avg)} avg`)
    }
    if (q.dtx_ratio != null) {
      lines.push(`DTX: ${(q.dtx_ratio * 100).toFixed(0)}% time`)
    }
    if (q.bitrate_avg_kbps != null) {
      lines.push(`Bitrate: ${Math.round(q.bitrate_avg_kbps)} kbps avg`)
    }
    const codec = q.end_codec ?? q.start_codec
    if (codec) lines.push(`Codec: ${codec}`)

    return lines.length > 0 ? lines.join('\n') : 'No quality data'
  }

  // Legacy path
  const lines: string[] = []
  if (q.start_rtt_ms !== null || q.end_rtt_ms !== null) {
    lines.push(`RTT: ${q.start_rtt_ms ?? '—'} → ${q.end_rtt_ms ?? '—'} ms`)
  }
  if (q.start_jitter_ms !== null || q.end_jitter_ms !== null) {
    lines.push(`Jitter: ${q.start_jitter_ms ?? '—'} → ${q.end_jitter_ms ?? '—'} ms`)
  }

  const fmtLoss = (lost: number | null, rcv: number | null): string => {
    if (lost === null || rcv === null) return '—'
    const total = lost + rcv
    if (total === 0) return '0%'
    return `${((lost / total) * 100).toFixed(1)}%`
  }
  const sLoss = fmtLoss(q.start_packets_lost, q.start_packets_received)
  const eLoss = fmtLoss(q.end_packets_lost, q.end_packets_received)
  if (sLoss !== '—' || eLoss !== '—') {
    lines.push(`Loss: ${sLoss} → ${eLoss}`)
  }

  if (q.end_bitrate_kbps !== null) {
    lines.push(`Bitrate: ${q.end_bitrate_kbps} kbps`)
  }
  const codec = q.end_codec ?? q.start_codec
  if (codec) lines.push(`Codec: ${codec}`)

  return lines.length > 0 ? lines.join('\n') : 'No quality data'
}

/**
 * Compact summary string for the admin call-history "Quality" column.
 * Format follows the plan:
 *   `84→112ms · 0/312→3/1843 loss · opus`
 * Missing halves are rendered as `?`. Entirely-null quality (legacy calls
 * before the feature) is represented with `—` by the caller (the column
 * renders `null` here as the full em-dash fallback).
 */
export function formatQualitySummary(q: CallQuality | null): string {
  if (!q) return '—'

  const startMs = q.start_rtt_ms
  const endMs = q.end_rtt_ms
  const rttPart = (startMs === null && endMs === null)
    ? null
    : `${startMs ?? '?'}→${endMs ?? '?'}ms`

  const sLost = q.start_packets_lost, sRcv = q.start_packets_received
  const eLost = q.end_packets_lost, eRcv = q.end_packets_received
  const lossStart = (sLost !== null && sRcv !== null) ? `${sLost}/${sRcv}` : '?'
  const lossEnd = (eLost !== null && eRcv !== null) ? `${eLost}/${eRcv}` : '?'
  const lossPart = (lossStart === '?' && lossEnd === '?')
    ? null
    : `${lossStart}→${lossEnd} loss`

  const codec = q.end_codec ?? q.start_codec ?? null

  const parts = [rttPart, lossPart, codec].filter(Boolean) as string[]
  return parts.length > 0 ? parts.join(' · ') : '—'
}
